import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDate, fmtMoney } from "../lib/format";
import { ChevronDown, ChevronRight, Plus, Download, FileText, Pencil, Upload, Sparkles, X, Loader2 } from "lucide-react";

const STATUS_TONE = {
  active: "pm-pill-green",
  negotiating: "pm-pill-amber",
  in_negotiation: "pm-pill-amber",
  transfer: "pm-pill-blue",
  expired: "pm-pill-slate",
  completed: "pm-pill-slate",
  archived: "pm-pill-slate",
};

const BONUS_TONE = {
  paid: "pm-pill-green",
  tracking: "pm-pill-amber",
  triggered: "pm-pill-blue",
  not_triggered: "pm-pill-slate",
};

function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}

function urgencyTier(days) {
  if (days == null) return null;
  if (days <= 30) return { label: "30d", tone: "pm-pill-red", barColor: "bg-red-500" };
  if (days <= 60) return { label: "60d", tone: "pm-pill-amber", barColor: "bg-amber-500" };
  if (days <= 90) return { label: "90d", tone: "pm-pill-amber", barColor: "bg-amber-400" };
  return null;
}

export default function ContractsGlobal() {
  const [summary, setSummary] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [openGroups, setOpenGroups] = useState({ active: true, negotiating: true, expired: false });

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/contracts-stats"),
      api.get("/contracts"),
    ]);
    setSummary(s.data);
    setContracts(c.data || []);
  };

  useEffect(() => { load(); }, []);

  // Deep-link: open a specific contract when the URL has `?open={id}` or
  // when react-router state carries openContractId. Used by the Dashboard
  // urgent feed and the notification bell.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  useEffect(() => {
    const fromQuery = searchParams.get("open");
    const fromState = location.state?.openContractId;
    const target = fromQuery || fromState;
    if (target && contracts.some((c) => c.id === target)) {
      setSelectedId(target);
      // Clean the URL so a refresh doesn't re-open every time.
      if (fromQuery) {
        const next = new URLSearchParams(searchParams);
        next.delete("open");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts]);

  useEffect(() => {
    if (!selectedId) { setTimeline([]); return; }
    api.get(`/contracts/${selectedId}/timeline`).then((r) => setTimeline(r.data || [])).catch(() => setTimeline([]));
  }, [selectedId]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const expiringSoon = useMemo(() => {
    return contracts.filter((c) => {
      if (c.status !== "active") return false;
      const d = daysBetween(today, c.expiry_date);
      return d != null && d >= 0 && d <= 90;
    }).sort((a, b) => (a.expiry_date || "").localeCompare(b.expiry_date || ""));
  }, [contracts, today]);

  const grouped = useMemo(() => {
    const g = { active: [], negotiating: [], expired: [] };
    contracts.forEach((c) => {
      const s = c.status || "active";
      if (s === "active") g.active.push(c);
      else if (s === "negotiating" || s === "in_negotiation" || s === "transfer") g.negotiating.push(c);
      else g.expired.push(c);
    });
    return g;
  }, [contracts]);

  const selected = useMemo(() => contracts.find((c) => c.id === selectedId), [contracts, selectedId]);

  return (
    <Layout title="Contracts" subtitle="Pipeline, renewals, commissions">
      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="contracts-stat-row">
        <StatCard label="Active contracts" value={summary?.active ?? "—"} testid="stat-active" />
        <StatCard label="Expiring in 90d" value={summary?.expiring_90d ?? "—"} tone={summary?.expiring_90d ? "red" : "default"} testid="stat-expiring-90d" />
        <StatCard label="In negotiation" value={summary?.in_negotiation ?? "—"} tone="amber" testid="stat-in-negotiation" />
        <StatCard label="Monthly commissions" value={summary?.monthly_commission_total != null ? fmtMoney(summary.monthly_commission_total) : "—"} tone="green" testid="stat-monthly-commission" />
      </div>

      {/* Expiring soon */}
      {expiringSoon.length > 0 && (
        <div className="pm-card p-6 mb-5" data-testid="expiring-soon-section">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Expiring soon — action required</div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-bold tracking-tight">Contracts requiring attention</h3>
            <span className="pm-pill pm-pill-red text-[11px]">{expiringSoon.length} urgent</span>
          </div>
          <div className="space-y-3">
            {expiringSoon.map((c) => <ExpiringRow key={c.id} c={c} today={today} onClick={() => setSelectedId(c.id)} />)}
          </div>
        </div>
      )}

      {/* Pipeline */}
      <div className="pm-card p-6 mb-5" data-testid="pipeline-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold tracking-tight">Contract pipeline</h3>
          <button className="pm-btn-secondary h-9 px-3 rounded-lg text-sm font-bold flex items-center gap-1.5" data-testid="new-contract-btn">
            <Plus size={14} /> New contract
          </button>
        </div>

        {contracts.length === 0 ? (
          <EmptyState text="No contracts recorded yet. Add your first contract to start tracking commission." cta="Add contract" />
        ) : (
          <div className="space-y-3">
            {[
              { key: "active", label: "Active contracts", rows: grouped.active },
              { key: "negotiating", label: "In negotiation", rows: grouped.negotiating },
              { key: "expired", label: "Expired", rows: grouped.expired },
            ].map((g) => (
              <PipelineGroup
                key={g.key}
                groupKey={g.key}
                label={g.label}
                rows={g.rows}
                open={openGroups[g.key]}
                onToggle={() => setOpenGroups((o) => ({ ...o, [g.key]: !o[g.key] }))}
                onRowClick={(c) => setSelectedId(c.id === selectedId ? null : c.id)}
                selectedId={selectedId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail */}
      {selected && (
        <ContractDetail
          contract={selected}
          timeline={timeline}
          onClose={() => setSelectedId(null)}
          onUpdated={load}
        />
      )}
    </Layout>
  );
}

function StatCard({ label, value, tone = "default", testid }) {
  const toneClass = {
    default: "text-slate-900",
    red: "text-red-600",
    amber: "text-amber-700",
    green: "text-emerald-700",
  }[tone];
  return (
    <div className="pm-card p-4" data-testid={testid}>
      <div className={`font-display font-black text-3xl font-mono-tab ${toneClass}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function ExpiringRow({ c, today, onClick }) {
  const daysRemaining = daysBetween(today, c.expiry_date);
  const totalDays = daysBetween(c.effective_date, c.expiry_date) || 0;
  const elapsed = totalDays - (daysRemaining || 0);
  const pct = totalDays > 0 ? Math.min(100, Math.max(0, (elapsed / totalDays) * 100)) : 0;
  const tier = urgencyTier(daysRemaining);
  const dateColor = daysRemaining != null && daysRemaining <= 30 ? "text-red-600" : "text-slate-800";

  return (
    <div className="rounded-xl border border-slate-200 p-3 hover:border-slate-300 hover:bg-slate-50/40 transition-all cursor-pointer"
         onClick={onClick} data-testid={`expiring-row-${c.id}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-slate-900">{c.client_name || "—"}</span>
            {tier && <span className={`pm-pill ${tier.tone} text-[10px]`}>{tier.label}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {c.client_position || "—"} · {c.counterparty || "—"} · expires {fmtDate(c.expiry_date)}
          </div>
          <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div className={`${tier?.barColor || "bg-slate-400"} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-mono-tab font-bold text-sm ${dateColor}`}>{fmtDate(c.expiry_date)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{daysRemaining != null ? `${daysRemaining} days` : "—"}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          className={daysRemaining != null && daysRemaining <= 30 ? "pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold" : "pm-btn-secondary h-9 px-4 rounded-lg text-xs font-bold"}
          data-testid={`expiring-action-${c.id}`}
        >
          {daysRemaining != null && daysRemaining <= 30 ? "Negotiate" : "View"}
        </button>
      </div>
    </div>
  );
}

function PipelineGroup({ groupKey, label, rows, open, onToggle, onRowClick, selectedId }) {
  const ChevIcon = open ? ChevronDown : ChevronRight;
  return (
    <div data-testid={`pipeline-group-${groupKey}`}>
      <button onClick={onToggle} className="flex items-center gap-2 w-full text-left py-2 hover:bg-slate-50/40 rounded transition-colors">
        <ChevIcon size={14} className="text-slate-500" />
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label} ({rows.length})</span>
      </button>
      {open && rows.length > 0 && (
        <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
          {rows.map((c) => {
            const s = c.status || "active";
            const isSelected = c.id === selectedId;
            return (
              <button
                key={c.id}
                onClick={() => onRowClick(c)}
                className={`grid grid-cols-12 gap-3 items-center w-full text-left px-4 py-3 hover:bg-slate-50/60 transition-colors ${isSelected ? "bg-emerald-50/40" : ""}`}
                data-testid={`pipeline-row-${c.id}`}
              >
                <div className="col-span-12 md:col-span-5">
                  <div className="font-display font-bold text-slate-900">{c.client_name || "—"}</div>
                  <div className="text-xs text-slate-500">{c.client_position || "—"} · {c.counterparty || "—"}</div>
                </div>
                <div className="col-span-6 md:col-span-3 font-mono-tab text-sm text-right md:text-right">
                  {c.base_wage ? `${fmtMoney(c.base_wage, c.currency)}/mo` : "—"}
                </div>
                <div className="col-span-3 md:col-span-2 font-mono-tab text-sm text-slate-700 text-right">
                  {fmtDate(c.expiry_date)}
                </div>
                <div className="col-span-3 md:col-span-2 text-right">
                  <span className={`pm-pill ${STATUS_TONE[s] || "pm-pill-slate"}`}>{s.replace("_", " ")}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {open && rows.length === 0 && (
        <div className="text-xs text-slate-400 italic px-4 py-2">No contracts in this group.</div>
      )}
    </div>
  );
}

function ContractDetail({ contract, timeline, onClose, onUpdated }) {
  const weekly = contract.base_wage ? Math.round((contract.base_wage * 12) / 52) : null;
  const annual = contract.base_wage ? contract.base_wage * 12 : null;
  const today = new Date().toISOString().slice(0, 10);
  const daysRemaining = daysBetween(today, contract.expiry_date);

  return (
    <div className="pm-card p-6 mb-5" data-testid="contract-detail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Contract detail — {contract.client_name || "—"}</div>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1">{contract.counterparty || "—"} — Player contract</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`pm-pill ${STATUS_TONE[contract.status || "active"]}`}>{(contract.status || "active").replace("_", " ")}</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 ml-2" data-testid="contract-detail-close">✕</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <FieldRow label="Club" value={contract.counterparty || "—"} />
        <FieldRow label="Start date" value={fmtDate(contract.effective_date)} />
        <FieldRow label="Expiry date" value={fmtDate(contract.expiry_date)} />
        <FieldRow label="Duration remaining" value={daysRemaining != null ? `${daysRemaining} days` : "—"} tone={daysRemaining != null && daysRemaining > 365 ? "green" : "default"} />
        <FieldRow label="Weekly wage" value={weekly ? `${fmtMoney(weekly, contract.currency)}/week` : "—"} />
        <FieldRow label="Monthly wage" value={contract.base_wage ? `${fmtMoney(contract.base_wage, contract.currency)}/month` : "—"} />
        <FieldRow label="Annual wage" value={annual ? `${fmtMoney(annual, contract.currency)}/year` : "—"} />
        <FieldRow label="Agent commission" value={contract.commission_pct ? `${contract.commission_pct}% of gross` : "—"} />
        <FieldRow label="Release clause" value={contract.release_clause ? fmtMoney(contract.release_clause, contract.currency) : "—"} />
        <FieldRow label="Option to extend" value={contract.option_to_extend || "—"} />
      </div>

      {/* Bonuses */}
      <div className="mt-6 pt-5 border-t border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-base font-bold tracking-tight">Performance bonuses</h4>
          <button className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid="add-bonus-btn">
            <Plus size={12} /> Add bonus
          </button>
        </div>
        {(!contract.bonuses || contract.bonuses.length === 0) ? (
          <div className="text-sm text-slate-500 italic py-2">No performance bonuses added. Add bonus clauses from the contract to track them here.</div>
        ) : (
          <div className="space-y-2">
            {contract.bonuses.map((b, i) => (
              <BonusRow key={i} bonus={b} contractId={contract.id} index={i} onUpdated={onUpdated} />
            ))}
          </div>
        )}
      </div>

      {/* Commission agreement OCR */}
      <CommissionOcrPanel contract={contract} onApplied={onUpdated} />

      {/* Action buttons */}
      <div className="mt-6 pt-5 border-t border-slate-200 flex flex-wrap gap-2">
        <button className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="download-contract-btn">
          <Download size={12} /> Download contract
        </button>
        <button className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="edit-contract-btn">
          <Pencil size={12} /> Edit
        </button>
        <button className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="add-addendum-btn">
          <FileText size={12} /> Add addendum
        </button>
        <button className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="renew-contract-btn">Renew</button>
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="mt-6 pt-5 border-t border-slate-200" data-testid="contract-timeline">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Contract history — timeline</div>
          <div className="space-y-4">
            {timeline.map((e, i) => <TimelineEvent key={`${e.id}-${i}`} event={e} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, value, tone = "default" }) {
  const t = { default: "text-slate-900", green: "text-emerald-700", red: "text-red-600" }[tone];
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
      <div className={`text-base font-mono-tab font-semibold mt-1 ${t}`}>{value}</div>
    </div>
  );
}

function BonusRow({ bonus, contractId, index, onUpdated }) {
  const status = bonus.status || "tracking";
  const updateStatus = async (newStatus) => {
    try {
      await api.patch(`/contracts/${contractId}/bonus-status`, { index, status: newStatus, paid_date: newStatus === "paid" ? new Date().toISOString().slice(0, 10) : null });
      onUpdated?.();
    } catch (e) {
      console.warn("Failed to update bonus", e);
    }
  };
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50/40" data-testid={`bonus-row-${index}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-900">{bonus.name || bonus.label || "—"}</div>
        <div className="text-xs text-slate-500">{bonus.trigger || "—"}</div>
      </div>
      <div className="font-mono-tab font-bold text-sm w-24 text-right">{bonus.amount ? fmtMoney(bonus.amount) : "—"}</div>
      <select
        value={status}
        onChange={(e) => updateStatus(e.target.value)}
        className={`pm-pill ${BONUS_TONE[status] || "pm-pill-slate"} text-[11px] border-0 cursor-pointer outline-none`}
        data-testid={`bonus-status-${index}`}
      >
        <option value="tracking">Tracking</option>
        <option value="triggered">Triggered</option>
        <option value="paid">Paid</option>
        <option value="not_triggered">Not triggered</option>
      </select>
    </div>
  );
}

function TimelineEvent({ event }) {
  const dotColor = {
    blue: "bg-blue-500",
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    gray: "bg-slate-400",
  }[event.tone] || "bg-slate-400";
  return (
    <div className="flex gap-3" data-testid={`timeline-event-${event.id}`}>
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full ${dotColor} mt-1.5`} />
        <div className="flex-1 w-px bg-slate-200 mt-1" />
      </div>
      <div className="flex-1 pb-3">
        <div className="font-semibold text-sm text-slate-900">{event.title}</div>
        {event.summary && <div className="text-xs text-slate-600 mt-0.5">{event.summary}</div>}
        <div className="text-xs text-slate-400 mt-1 font-mono-tab">{fmtDate(event.date)}</div>
      </div>
    </div>
  );
}

function EmptyState({ text, cta }) {
  return (
    <div className="text-center py-12">
      <div className="text-sm text-slate-500 mb-3">{text}</div>
      {cta && <button className="pm-btn-primary h-10 px-4 rounded-lg text-sm font-bold inline-flex items-center gap-2"><Plus size={14} /> {cta}</button>}
    </div>
  );
}

/* ─────────────────────────────── Commission agreement OCR ─────────────────────────────── */

function CommissionOcrPanel({ contract, onApplied }) {
  const [status, setStatus] = useState({ loading: true, has_extract: false });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(`/contracts/${contract.id}/commission-ocr`);
      setStatus({ loading: false, ...r.data });
    } catch (e) {
      setStatus({ loading: false, has_extract: false });
    }
  };

  useEffect(() => { refresh(); }, [contract.id]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/contracts/${contract.id}/commission-ocr`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus({ loading: false, has_extract: true, ...r.data, filename: file.name, mime: file.type });
      setReviewOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-slate-200" data-testid="commission-ocr-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display text-base font-bold tracking-tight flex items-center gap-2">
          <Sparkles size={14} className="text-emerald-600" />
          Commission agreement
          <span className="pm-pill pm-pill-slate text-[10px]">AI extract</span>
        </h4>
        {status.has_extract && (
          <button
            onClick={() => setReviewOpen(true)}
            className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5"
            data-testid="ocr-review-btn"
          >
            <FileText size={11} /> Review extracted terms
          </button>
        )}
      </div>

      {status.has_extract ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm" data-testid="ocr-status-extracted">
          <div className="flex items-start gap-2">
            <span className="text-emerald-600">✓</span>
            <div className="flex-1">
              <div className="font-bold text-emerald-900">
                Agreement scanned · {status.filename || "(file)"}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                {status.pages_processed} page{status.pages_processed === 1 ? "" : "s"} processed
                {status.run_by_name ? ` by ${status.run_by_name}` : ""}
                {status.run_at ? ` · ${new Date(status.run_at).toLocaleDateString("en-GB")}` : ""}
              </div>
            </div>
            <span className={`pm-pill text-[10px] ${
              (status.extract?.confidence || "").toLowerCase() === "high" ? "pm-pill-green" :
              (status.extract?.confidence || "").toLowerCase() === "low" ? "pm-pill-amber" :
              "pm-pill-slate"
            }`}>
              {(status.extract?.confidence || "—").toUpperCase()} CONFIDENCE
            </span>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-slate-200 px-4 py-5 text-sm text-center" data-testid="ocr-status-empty">
          <div className="text-slate-500 mb-3">
            Upload a signed commission agreement (PDF or image) — Playmaker uses Claude vision to extract the financial terms and generate the payment schedule automatically.
          </div>
          <label className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "Extracting…" : "Upload agreement"}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={handleFile}
              className="hidden"
              data-testid="ocr-file-input"
              disabled={uploading}
            />
          </label>
        </div>
      )}

      {error && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="ocr-error">
          {error}
        </div>
      )}

      {reviewOpen && (
        <CommissionOcrReviewModal
          contract={contract}
          extract={status.extract}
          onClose={() => setReviewOpen(false)}
          onApplied={() => {
            setReviewOpen(false);
            refresh();
            onApplied && onApplied();
          }}
        />
      )}
    </div>
  );
}

const OCR_FIELDS = [
  { key: "agent_commission_pct", label: "Agent commission", suffix: "%", type: "number", step: "0.1" },
  { key: "payment_schedule", label: "Payment schedule", type: "text" },
  { key: "signing_fee_amount", label: "Signing fee amount", prefix: "€", type: "number" },
  { key: "signing_fee_due_days", label: "Signing fee due (days after signing)", type: "number" },
  { key: "sell_on_pct", label: "Sell-on clause", suffix: "%", type: "number", step: "0.1" },
  { key: "instalment_count", label: "Instalments (1, 2 or 4)", type: "number", min: 1, max: 4 },
  { key: "total_commission_value", label: "Total commission value", prefix: "€", type: "number" },
  { key: "currency", label: "Currency", type: "text" },
  { key: "governing_law", label: "Governing law", type: "text" },
];

const LOW_CONF_KEYS_FALLBACK = ["payment_schedule", "governing_law"];

function CommissionOcrReviewModal({ contract, extract, onClose, onApplied }) {
  const [values, setValues] = useState(() => {
    const init = {};
    OCR_FIELDS.forEach((f) => { init[f.key] = extract?.[f.key] ?? ""; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const warnings = extract?.warnings || [];
  // Treat any field listed in warnings — or any null on a typically-present field — as amber.
  const isLowConf = (k) => warnings.some((w) => (w || "").toLowerCase().includes(k.replace(/_/g, " ")))
    || (LOW_CONF_KEYS_FALLBACK.includes(k) && !values[k]);

  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const onSave = async () => {
    setError(null);
    setSaving(true);
    try {
      // Convert numeric strings to numbers; drop empties
      const payload = { regenerate_schedule: true };
      OCR_FIELDS.forEach((f) => {
        const v = values[f.key];
        if (v === "" || v == null) return;
        if (f.type === "number") {
          const n = Number(v);
          if (!Number.isNaN(n)) payload[f.key] = n;
        } else {
          payload[f.key] = v;
        }
      });
      const r = await api.post(`/contracts/${contract.id}/commission-ocr/apply`, payload);
      onApplied(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const parties = extract?.payment_parties;

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="ocr-review-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-600">Commission agreement scanned</div>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1">Verify extracted terms</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="ocr-review-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="text-sm text-slate-600 leading-relaxed">
            Claude extracted the following terms from the agreement. Fields highlighted{" "}
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold text-xs">amber</span>{" "}
            were ambiguous — please verify against the original document before saving.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {OCR_FIELDS.map((f) => {
              const amber = isLowConf(f.key);
              return (
                <label key={f.key} className="block">
                  <div className={`text-[11px] uppercase tracking-[0.18em] font-bold mb-1 ${amber ? "text-amber-700" : "text-slate-500"}`}>
                    {f.label}
                  </div>
                  <div className={`flex items-center gap-1 rounded-lg border-2 px-3 h-10 transition-colors ${amber ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"}`}>
                    {f.prefix && <span className="text-slate-400 text-sm">{f.prefix}</span>}
                    <input
                      type={f.type}
                      step={f.step}
                      min={f.min}
                      max={f.max}
                      value={values[f.key] ?? ""}
                      onChange={(e) => onChange(f.key, e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm font-mono-tab"
                      data-testid={`ocr-field-${f.key}`}
                    />
                    {f.suffix && <span className="text-slate-400 text-sm">{f.suffix}</span>}
                  </div>
                </label>
              );
            })}
          </div>

          {parties && parties.length > 0 && (
            <div className="rounded-xl border border-slate-200 px-4 py-3 bg-slate-50/60" data-testid="ocr-parties-preview">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">
                Payment parties detected
              </div>
              <ul className="space-y-1.5 text-sm">
                {parties.map((p, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span className="text-slate-800">{p.name}</span>
                    <span className="font-mono-tab font-bold text-slate-700">{p.percentage}%</span>
                  </li>
                ))}
              </ul>
              <div className="text-[11px] text-slate-500 mt-2 italic">
                These will be pre-populated as commission splits in Phase 3.
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm" data-testid="ocr-warnings">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-1">
                Extraction warnings
              </div>
              <ul className="list-disc pl-5 space-y-0.5 text-amber-900">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-sm font-bold" data-testid="ocr-cancel-btn">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="pm-btn-primary h-10 px-5 rounded-lg text-sm font-bold flex items-center gap-1.5 disabled:opacity-60"
            data-testid="ocr-save-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : null}
            {saving ? "Saving…" : "Save commission terms"}
          </button>
        </div>
      </div>
    </div>
  );
}
